import React, { useEffect, useState } from 'react';
import { resizeForSurface } from '../../utils/imageSource';
import { buildPhotoSrcSet } from '../../utils/photoSrcSet';

// One photo's turn. The drift (tailwind `hero-drift`, 10s) outlasts it, so a
// photo is still moving while the next fades in over it.
const TURN_MS = 7000;
const SIZES = '100vw';
// Each photo drifts toward a different point, so the set never reads as one
// zoom repeated.
const ORIGINS = ['30% 35%', '70% 40%', '50% 70%', '25% 60%', '75% 30%', '50% 45%'];

// Keyed on the URL, not its place in the set: a set that arrives again in
// another order must not move the zoom of the photo on screen.
const originFor = (url) => {
  let h = 0;
  for (let i = 0; i < url.length; i += 1) h = (h * 31 + url.charCodeAt(i)) | 0;
  return ORIGINS[Math.abs(h) % ORIGINS.length];
};

const reducedMotion = () => typeof window !== 'undefined'
  && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * The Home hero's photo: one photo still, or a trip's set drifting slowly and
 * fading one into the next (HomeHero passes the set only for a trip under way
 * or just over). The next photo is decoded before its turn — with the srcset
 * and sizes the <img> uses, so the file decoded is the file shown — and a
 * slow connection holds the current one rather than fading to a blank; a
 * photo that fails to load drops out of the set. Holds still under reduced
 * motion and while the tab is hidden. `overlay(src)` draws what sits over the
 * photo (scrim, credit) for the photo on screen, so the credit follows it.
 *
 * The turn is tracked by URL, not position: a photo dropping out of the set,
 * or the set arriving again in another order, never moves the photo on screen.
 */
export default function HeroPhotoDrift({ photos, overlay }) {
  const [broken, setBroken] = useState(() => new Set());
  const set = photos.filter(url => !broken.has(url));
  const setKey = set.join('\n');
  const [turn, setTurn] = useState({ url: null, prevUrl: null, gen: 0 });
  const current = set.includes(turn.url) ? turn.url : (set[0] || null);
  const prev = turn.prevUrl && turn.prevUrl !== current && set.includes(turn.prevUrl) ? turn.prevUrl : null;
  const animate = set.length > 1 && !reducedMotion();

  useEffect(() => {
    if (!animate || !current) return undefined;
    let cancelled = false;
    let busy = false;
    const timer = setInterval(() => {
      // One decode at a time: a slow one must not start a second advance.
      if (busy || document.hidden) return;
      const next = set[(set.indexOf(current) + 1) % set.length];
      const probe = new Image();
      const srcSet = buildPhotoSrcSet(next);
      if (srcSet) {
        probe.sizes = SIZES;
        probe.srcset = srcSet;
      }
      probe.src = resizeForSurface(next, 'large');
      busy = true;
      (probe.decode ? probe.decode() : Promise.resolve())
        .then(() => {
          if (cancelled) return;
          // Advance only from the photo this tick started on — read the way
          // render reads it, so a turn whose photo left the set still moves.
          setTurn(t => ((set.includes(t.url) ? t.url : set[0]) === current
            ? { url: next, prevUrl: current, gen: t.gen + 1 }
            : t));
        })
        .catch(() => {
          if (!cancelled) setBroken(b => new Set([...b, next]));
        })
        .finally(() => { busy = false; });
    }, TURN_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [animate, current, setKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!current) return null;

  return (
    <>
      {/* Its own stacking context: the fading photo's z-index stays under
          the overlay. */}
      <div className="absolute inset-0 isolate">
        {/* The previous photo stays under the one fading in (same key as on
            its own turn, so its drift carries on), then drops out. */}
        {[prev, current].filter(Boolean).map(url => {
          const active = url === current;
          const srcSet = buildPhotoSrcSet(url);
          return (
            <div
              key={`${url}|${active ? turn.gen : turn.gen - 1}`}
              className={`absolute inset-0 overflow-hidden ${active ? 'z-[1]' : 'z-0'} ${animate && active && turn.gen > 0 ? 'animate-hero-fade-in' : ''}`}
            >
              <img
                src={resizeForSurface(url, 'large')}
                srcSet={srcSet || undefined}
                sizes={srcSet ? SIZES : undefined}
                alt=""
                onError={() => setBroken(b => new Set([...b, url]))}
                className={`w-full h-full object-cover ${animate ? 'animate-hero-drift' : ''}`}
                style={animate ? { transformOrigin: originFor(url) } : undefined}
              />
            </div>
          );
        })}
      </div>
      {overlay(resizeForSurface(current, 'large'))}
    </>
  );
}
